
import { SubscriptionPlan, SubscriptionTier, StoreSubscription, Store } from '../types';
import { storage } from '../lib/storage';

export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, SubscriptionPlan> = {
  STARTER: {
    id: 'STARTER',
    name: 'Starter',
    price: 0,
    productLimit: 5,
    features: ['5 Products Max', 'Standard Branding', 'Lynq Assistant'],
    color: '#64748b'
  },
  PRO: {
    id: 'PRO',
    name: 'Professional',
    price: 49,
    productLimit: 50,
    features: ['50 Products Max', 'Glassmorphism UI', 'Priority Support', 'Advanced Analytics'],
    color: '#2563eb'
  },
  ENTERPRISE: {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    price: 199,
    productLimit: 10000,
    features: ['Unlimited Products', 'Full Custom Branding', 'AI Video Ad Generation', '0% Platform Fee'],
    color: '#7c3aed'
  }
};

export const billingService = {
  getStorePlan: (store: Store): SubscriptionPlan => {
    const tier = store.subscription?.tier || 'STARTER';
    return SUBSCRIPTION_PLANS[tier];
  },

  upgradePlan: async (storeId: string, tier: SubscriptionTier) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const stores = storage.getStores();
    const updated = stores.map(s => {
      if (s.id === storeId) {
        const sub: StoreSubscription = {
          tier,
          startDate: new Date().toISOString(),
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          lastBilledAt: new Date().toISOString(),
          autoRenew: true,
          status: 'ACTIVE'
        };
        return { ...s, subscription: sub };
      }
      return s;
    });
    
    storage.saveStores(updated);
    window.dispatchEvent(new Event('store-branding-update'));
    return true;
  },

  processBillingCycle: (storeId: string) => {
    const store = storage.getStoreConfig(storeId);
    if (!store || !store.subscription) return;

    const now = new Date();
    const expiry = new Date(store.subscription.expiryDate);

    if (now > expiry && store.subscription.status !== 'OVERDUE') {
      const updatedSub = { ...store.subscription, status: 'OVERDUE' as const };
      const updatedStore = { ...store, subscription: updatedSub };
      storage.saveStoreConfig(updatedStore);
    }
  },

  payMonthlyFee: async (storeId: string) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const store = storage.getStoreConfig(storeId);
    if (!store.subscription) return;

    const plan = SUBSCRIPTION_PLANS[store.subscription.tier];
    const newExpiry = new Date(new Date(store.subscription.expiryDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

    const updatedSub: StoreSubscription = {
      ...store.subscription,
      expiryDate: newExpiry,
      lastBilledAt: new Date().toISOString(),
      status: 'ACTIVE'
    };

    storage.saveStoreConfig({ ...store, subscription: updatedSub });
    return true;
  },

  checkLimit: (storeId: string, type: 'product'): boolean => {
    const store = storage.getStoreConfig(storeId);
    const plan = billingService.getStorePlan(store);
    
    if (type === 'product') {
      const currentCount = storage.getProducts().filter(p => p.storeId === storeId).length;
      return currentCount < plan.productLimit;
    }
    
    return true;
  }
};
